import React from 'react'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { formatDateTime } from './apiData'

const isSameDay = (value, reference = new Date()) => {
  if (!value) return false
  const date = new Date(value)
  return !Number.isNaN(date.getTime())
    && date.getFullYear() === reference.getFullYear()
    && date.getMonth() === reference.getMonth()
    && date.getDate() === reference.getDate()
}

const getStatusTone = (status) => {
  const normalized = String(status || '').toUpperCase()
  if (['SUCCESS', 'COMPLETED', 'COMPLETADO'].includes(normalized)) return 'success'
  if (['WARNING', 'PARTIAL', 'CON_ADVERTENCIAS'].includes(normalized)) return 'warning'
  if (['FAILED', 'ERROR', 'CRITICAL'].includes(normalized)) return 'error'
  return 'info'
}

const getStatusLabel = (status) => {
  const normalized = String(status || '').toUpperCase()
  if (['SUCCESS', 'COMPLETED'].includes(normalized)) return 'Completado'
  if (normalized === 'PARTIAL') return 'Parcial'
  if (normalized === 'FAILED') return 'Con error'
  if (normalized === 'RUNNING') return 'En curso'
  if (normalized === 'PENDING') return 'Pendiente'
  return status || 'Sin estado'
}

const getExecutionStart = (execution) => (
  execution.startedAt || execution.started_at || execution.createdAt || execution.created_at
)

const getExecutionEnd = (execution) => (
  execution.finishedAt || execution.finished_at || execution.updatedAt || execution.updated_at
)

const getExecutionRecords = (execution) => (
  execution.recordsRead
  ?? execution.records_read
  ?? execution.recordsCreated
  ?? execution.records_created
  ?? execution.recordsUpdated
  ?? execution.records_updated
  ?? execution.records
  ?? 0
)

const getDuration = (execution) => {
  if (execution.duration) return execution.duration
  const start = new Date(getExecutionStart(execution))
  const end = new Date(getExecutionEnd(execution))
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '-'
  const seconds = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000))
  return new Date(seconds * 1000).toISOString().slice(11, 19)
}

const getScheduleFrequency = (schedule) => {
  if (schedule.scheduleType === 'INTERVAL' && schedule.frequencyMinutes) return `Cada ${schedule.frequencyMinutes} min`
  if (schedule.cronExpression) return schedule.cronExpression
  return schedule.scheduleType || 'Programada'
}

export const flattenIntegrationExecutions = (integrations = []) => (
  integrations.flatMap((integration) => (
    (integration.executions || []).map((execution) => {
      const status = execution.status || execution.executionStatus
      const start = getExecutionStart(execution)
      const tone = getStatusTone(status)

      return {
        id: execution.id || `${integration.id}-${start || integration.code}`,
        process: integration.name || integration.code,
        integration: integration.code,
        type: integration.integrationType || integration.type || integration.direction || '-',
        start,
        end: getExecutionEnd(execution),
        startLabel: formatDateTime(start, 'Sin fecha'),
        endLabel: formatDateTime(getExecutionEnd(execution), '-'),
        status: getStatusLabel(status),
        tone,
        records: new Intl.NumberFormat('es-ES').format(getExecutionRecords(execution)),
        recordsValue: Number(getExecutionRecords(execution)) || 0,
        duration: getDuration(execution),
        errorMessage: execution.errorSummary || execution.error_summary || execution.errorMessage || execution.error_message || '',
        retries: execution.retryCount ?? execution.retries ?? '-',
        owner: integration.system || 'Integraciones',
        icon: tone === 'error' ? <ErrorOutlineRoundedIcon /> : tone === 'warning' ? <WarningAmberRoundedIcon /> : <CheckCircleOutlineRoundedIcon />,
        raw: execution,
      }
    })
  )).sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0))
)

export const getTodayExecutions = (integrations = []) => (
  flattenIntegrationExecutions(integrations).filter((execution) => isSameDay(execution.start))
)

export const flattenIntegrationSchedules = (integrations = []) => (
  integrations.flatMap((integration) => (
    (integration.schedules || []).map((schedule) => {
      const nextRun = schedule.nextRunAt || schedule.next_run_at
      const date = new Date(nextRun)
      const time = Number.isNaN(date.getTime())
        ? 'Sin fecha'
        : new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' }).format(date)

      return {
        id: schedule.id || `${integration.id}-${nextRun || integration.code}`,
        time,
        process: integration.name || integration.code,
        integration: integration.code,
        frequency: getScheduleFrequency(schedule),
        nextRun,
        nextWindow: time === 'Sin fecha' ? '-' : time,
        status: schedule.active === false ? 'Pausada' : 'Programada',
        tone: schedule.active === false ? 'warning' : 'success',
        icon: <AccessTimeRoundedIcon />,
      }
    })
  )).sort((a, b) => new Date(a.nextRun || 0) - new Date(b.nextRun || 0))
)

export const getUpcomingSchedules = (integrations = []) => (
  flattenIntegrationSchedules(integrations).filter((schedule) => schedule.nextRun)
)

export const flattenIntegrationAlerts = (integrations = []) => {
  const executionErrors = flattenIntegrationExecutions(integrations)
    .filter((execution) => ['error', 'warning'].includes(execution.tone))
    .map((execution) => ({
      id: execution.id,
      process: execution.process,
      integration: execution.integration,
      time: execution.startLabel,
      severity: execution.tone === 'error' ? 'Error' : 'Advertencia',
      tone: execution.tone,
      message: execution.errorMessage || execution.status,
      retries: execution.retries,
      owner: execution.owner,
      icon: execution.icon,
    }))

  const integrationAlerts = integrations.flatMap((integration) => (
    (integration.alerts || []).map((alert) => {
      const severity = String(alert.severity || 'INFO').toUpperCase()
      const tone = severity === 'ERROR' || severity === 'CRITICAL' ? 'error' : severity === 'WARNING' ? 'warning' : 'info'
      return {
        id: alert.id || `${integration.id}-${alert.message}`,
        process: integration.name || integration.code,
        integration: integration.code,
        time: formatDateTime(alert.createdAt || alert.created_at, 'Sin fecha'),
        severity: tone === 'error' ? 'Error' : tone === 'warning' ? 'Advertencia' : 'Info',
        tone,
        message: alert.message || alert.alertType || 'Alerta de integración',
        retries: '-',
        owner: integration.system || 'Integraciones',
        icon: tone === 'error' ? <ErrorOutlineRoundedIcon /> : tone === 'warning' ? <WarningAmberRoundedIcon /> : <CheckCircleOutlineRoundedIcon />,
      }
    })
  ))

  return [...executionErrors, ...integrationAlerts]
}

export const buildExecutionMetrics = (executions = []) => {
  const completed = executions.filter((execution) => execution.tone === 'success').length
  const warnings = executions.filter((execution) => execution.tone === 'warning').length
  const errors = executions.filter((execution) => execution.tone === 'error').length
  const records = executions.reduce((total, execution) => total + execution.recordsValue, 0)

  return { completed, warnings, errors, records }
}

export const getProcessChartBackground = (summary, total) => {
  if (total <= 0) return '#e8eaed'

  let accumulated = 0
  const segments = summary.map((item) => {
    const start = (accumulated / total) * 100
    accumulated += Number(item.value)
    const end = (accumulated / total) * 100
    return `${item.color} ${start}% ${end}%`
  })

  return `conic-gradient(${segments.join(', ')})`
}
